// Foot — dorsal (top of the foot) detail panel for pain capture
// (PAIN_TRACKING_PLAN.md Task 3.2). React-free / JSX-free (hazard H7):
// scripts/validatePainMap.ts and scripts/renderBodyMap.ts import this module
// directly from the repo root. JSX starts in PainMapCanvas.tsx.
//
// Own canvas (D-PAIN-6, "Canvas conventions"): 0 0 160 320, centerline x = 80.
// ONE left foot is drawn; PainMapCanvas mirrors it to the right foot at render
// time via mirrorPath, so there is never a hand-authored right-foot copy. Every
// region is therefore a single "-l" region (a foot panel draws one limb only —
// the validator's paired rule S4 applies to front/back exclusively).
//
// Layout: toes point up (toe tips near y = 22, MTP / ball row near y = 90),
// midfoot y 90-235, hindfoot / heel y 235-306. This is a LEFT foot seen from
// ABOVE, so the big toe (medial) is on the viewer's RIGHT (high x) and the
// little toe (lateral) on the viewer's LEFT (low x) — the same convention the
// palmar hand panel uses for the thumb.
//
// Coverage (Task 3.2, dorsal aspect): MTP joint per toe, metatarsal heads
// (1st / 2nd / 5th — the common metatarsalgia and stress-fracture sites),
// ankle (talocrural) and subtalar joints, calcaneus, the common peroneal /
// deep peroneal nerve track running onto the dorsum toward the first web
// space, and the extensor tendon bands fanning from the ankle to the toes.

import type { PainStructure } from "@medassist/shared";
import { PAIN_STRUCTURE_META } from "@medassist/shared";
import type { PainRegion } from "./painRegions.js";
import { ellipsePath } from "./painRegions.js";

export const FOOT_DORSAL_VIEWBOX = "0 0 160 320";

/** A closed rounded-rectangle outline path (foot body / heel blocks). */
function box(x0: number, y0: number, x1: number, y1: number, r: number): string {
  return [
    `M ${x0 + r} ${y0}`,
    `L ${x1 - r} ${y0}`,
    `Q ${x1} ${y0} ${x1} ${y0 + r}`,
    `L ${x1} ${y1 - r}`,
    `Q ${x1} ${y1} ${x1 - r} ${y1}`,
    `L ${x0 + r} ${y1}`,
    `Q ${x0} ${y1} ${x0} ${y1 - r}`,
    `L ${x0} ${y0 + r}`,
    `Q ${x0} ${y0} ${x0 + r} ${y0}`,
    "Z",
  ].join(" ");
}

/** A rounded toe capsule from its tip (yTop) down to the ball of the foot. */
function toe(cx: number, yTop: number, w: number): string {
  const yBase = 74;
  return [
    `M ${cx - w} ${yBase}`,
    `L ${cx - w} ${yTop + 6}`,
    `Q ${cx - w} ${yTop} ${cx} ${yTop}`,
    `Q ${cx + w} ${yTop} ${cx + w} ${yTop + 6}`,
    `L ${cx + w} ${yBase}`,
    "Z",
  ].join(" ");
}

export const FOOT_DORSAL_OUTLINE: string[] = [
  box(44, 58, 118, 250, 22), // forefoot + midfoot
  box(56, 235, 106, 306, 24), // hindfoot / heel
  toe(110, 26, 8), // big toe (T1) — medial, viewer's right
  toe(93, 22, 6), // T2
  toe(77, 26, 6), // T3
  toe(62, 34, 6), // T4
  toe(49, 44, 6), // little toe (T5) — lateral, viewer's left
];

/** One left-foot-only region. `kind` is read from PAIN_STRUCTURE_META so a
 *  region can never disagree with the taxonomy (validator rule S7). */
function reg(id: string, structure: PainStructure, d: string): PainRegion {
  return { id, structure, kind: PAIN_STRUCTURE_META[structure].kind, side: "left", d };
}

// Toe ray centrelines (lateral little toe on the viewer's left -> medial big
// toe on the viewer's right).
const T5 = 49; // little
const T4 = 62;
const T3 = 77;
const T2 = 93;
const T1 = 110; // big toe / hallux

export const FOOT_DORSAL_STRUCTURES: PainRegion[] = [
  // ── MTP row (base of each toe — the "ball" of the foot) ────────────────────
  reg("joint-mtp-t1-l", "joint-mtp", ellipsePath(T1, 92, 6, 5.5)),
  reg("joint-mtp-t2-l", "joint-mtp", ellipsePath(T2, 86, 4.5, 4)),
  reg("joint-mtp-t3-l", "joint-mtp", ellipsePath(T3, 88, 4.5, 4)),
  reg("joint-mtp-t4-l", "joint-mtp", ellipsePath(T4, 92, 4.5, 4)),
  reg("joint-mtp-t5-l", "joint-mtp", ellipsePath(T5, 98, 4.5, 4)),

  // ── metatarsal heads (just proximal to the MTP joints) ────────────────────
  reg("bone-metatarsal-mt1-l", "bone-metatarsal", ellipsePath(108, 112, 6, 8)),
  reg("bone-metatarsal-mt2-l", "bone-metatarsal", ellipsePath(92, 108, 5, 8)),
  reg("bone-metatarsal-mt5-l", "bone-metatarsal", ellipsePath(52, 116, 5, 8)),

  // ── hindfoot joints + heel bone ──────────────────────────────────────────
  reg("joint-ankle-l", "joint-ankle", ellipsePath(82, 232, 16, 10)),
  reg("joint-subtalar-l", "joint-subtalar", ellipsePath(82, 254, 12, 8)),
  reg("bone-calcaneus-l", "bone-calcaneus", ellipsePath(80, 285, 16, 14)),

  // ── nerve ────────────────────────────────────────────────────────────────
  // Common / deep peroneal: front of the ankle onto the dorsum toward the
  // first web space between the big and second toes.
  reg("nerve-peroneal-l", "nerve-peroneal", ellipsePath(84, 155, 7, 62)),

  // ── soft tissue (extensor tendon bands over the top of the foot) ──────────
  reg("tissue-tendon-ext-med-l", "tissue-tendon", ellipsePath(96, 150, 5, 55)),
  reg("tissue-tendon-ext-lat-l", "tissue-tendon", ellipsePath(70, 150, 5, 52)),
];
